import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Facebook, Youtube, X } from 'lucide-react'; 
import { motion, AnimatePresence } from 'framer-motion';
import { initPaddle } from './utils/paddleClient';

// UPDATED IMPORTS: Added googleProvider and signInWithPopup
import { db, auth, googleProvider } from './firebase'; 
import {
  signInWithPopup, 
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  ref as dbRef,
  onValue,
  push,
  set,
  update,
  get
} from 'firebase/database';

import Navigation from './components/Navigation';
import LandingPage from './components/LandingPage';
import AdminPanel from './components/AdminPanel'; 
import AdminNavigation from './components/admin/AdminNavigation';
import Toast from './components/Toast';
import PaymentSuccess from './pages/payment-success';
import PaymentCancel from './pages/payment-cancel';
import PaymentSuccessArtist from './pages/payment-success-artist';
import PaymentCancelArtist from './pages/payment-cancel-artist';
import { usePurchases } from './components/music/hooks/usePurchases';
import SchedulesListPage from './pages/schedules';
import ScheduleBookingModal from './pages/modals/ScheduleBookingModal';
import DonationModal from './pages/modals/DonationModal';
import { AudioPlayerProvider } from './components/music/AudioPlayerProvider';

// NEW IMPORTS: Artist-related components
import ArtistSubscriptionModal from './components/artist/ArtistSubscriptionModal';
import ArtistDashboard from './components/artist/ArtistDashboard';
import ArtistDashboardPage from './pages/artist-dashboard';
import { useArtistSubscription } from './components/artist/hooks/useArtistSubscription';

// Page imports
import MusicPage from './components/music';
import VideosPage from './components/videos';
import GalleryPage from './components/gallery';
import MembersPage from './components/members';
import PurchasesPage from './pages/purchases';
import AllPurchasesPage from './pages/all-purchases';

// Authorized admin emails
const AUTHORIZED_ADMIN_EMAILS = [
    'asidorx18@gmail.com',
    'hulyray@gmail.com',
    'winnerasidor23@gmail.com',
];

// Save account details to the users table on register/sign-in
const syncUserProfile = async (user, isNewUser = false) => {
    if (!user || !user.uid) return;

    const userRef = dbRef(db, `users/${user.uid}`);
    const providerData = user.providerData && user.providerData[0];

    try {
        const snapshot = await get(userRef);
        const existing = snapshot.exists() ? snapshot.val() : {};

        const updates = {
            displayName: user.displayName || '',
            email: user.email || '',
            photoURL: user.photoURL || '',
            provider: (providerData && providerData.providerId) || 'google',
            lastLoginAt: new Date().toISOString(),
        };

        if (isNewUser || !existing.createdAt) {
            updates.createdAt = new Date().toISOString();
        }

        await update(userRef, updates);
    } catch (err) {
        console.error('Error syncing user profile:', err);
    }
};

function PublicView({ 
  heroImageUrl, 
  gallery, 
  music, 
  videos, 
  members, 
  schedules,
  loading,
  currentUser,
  onArtistRegisterClick,
  onBookScheduleClick,
  onDonateClick
}) {
  return (
    <LandingPage
      heroImageUrl={heroImageUrl}
      gallery={gallery}
      music={music}
      videos={videos}
      members={members}
      schedules={schedules}
      loading={loading}
      currentUser={currentUser}
      onArtistRegisterClick={onArtistRegisterClick}
      onBookScheduleClick={onBookScheduleClick}
      onDonateClick={onDonateClick}
    />
  );
}

function AdminView({ 
  db, 
  showToast, 
  videos, 
  music, 
  members, 
  gallery, 
  heroImageUrl,
  currentUser
}) {
  return (
    <AdminPanel
      db={db}
      showToast={showToast}
      videos={videos}
      music={music}
      members={members}
      gallery={gallery}
      heroImageUrl={heroImageUrl}
      currentUser={currentUser}
    />
  );
}

const Footer = () => {
  const socialLinks = [
    { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/THEASIDORS' },
    { name: 'YouTube', icon: Youtube, url: 'https://youtube.com/channel/UCRgQJ0bmIK5QWSiabN7zFvA' },
  ];

  return (
    <footer className="bg-[#0a0b0e] text-white py-12 px-6 border-t border-white/10 font-sans relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-[#bd9f67]/5 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto flex flex-col items-center space-y-8 relative z-10">
        
        {/* Brand/Logo Subheading */}
        <div className="flex flex-col items-center text-center space-y-2">
          <img
            src="/images/asidors_logo_white.png"
            alt="The AsidorS Logo"
            className="h-10 w-auto opacity-90"
          />
          <p className="text-xs text-white/60 tracking-wider uppercase">
            Gospel Music • Ministry • Inspiration
          </p>
        </div>

        {/* Social Links */}
        <div className="flex items-center gap-4">
          {socialLinks.map((link) => (
            <a 
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              title={`Visit The AsidorS on ${link.name}`}
              className="p-3 rounded-full bg-white/5 border border-white/10 text-white/70 hover:text-[#bd9f67] hover:border-[#bd9f67]/40 hover:bg-[#bd9f67]/10 transition-all duration-300"
            >
              <link.icon size={20} strokeWidth={1.5} />
            </a>
          ))}
        </div>
        
        {/* Contact Info */}
        <div className="text-center text-xs text-white/60 space-y-1 font-mono">
          <p className="text-white/80 font-sans font-medium">Contact Us:</p>
          <p>Email: theasidorshome@yahoo.com</p>
          <p>Phone: +1 360-998-0121</p>
        </div>
        
        {/* Copyright */}
        <div className="pt-4 border-t border-white/5 w-full text-center">
          <p className="text-xs text-white/40">
            &copy; {new Date().getFullYear()} The AsidorS Home. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

function AppContent() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [isProcessingSchedule, setIsProcessingSchedule] = useState(false);
  
  // Artist subscription state
  const [showArtistSubscription, setShowArtistSubscription] = useState(false);
  const [showArtistDashboard, setShowArtistDashboard] = useState(false);
  const [artistData, setArtistData] = useState(null);
  
  const [videos, setVideos] = useState([]);
  const [music, setMusic] = useState([]);
  const [members, setMembers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [loading, setLoading] = useState(true);

  // Use the purchases hook
  const purchases = usePurchases(currentUser);
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    const setupPaddle = async () => {
      const initialized = await initPaddle();
      if (initialized) {
        console.log('✅ Paddle checkout ready');
      }
    };
    setupPaddle();
  }, []);
  
  // Artist subscription hook
  const { 
    createSubscription, 
    loading: isProcessingArtist, 
  } = useArtistSubscription();

  const handleNavigationAdminClick = () => {
    if (isAdmin) {
      navigate('/admin?tab=dashboard');
    } else {
      setShowAdminLogin(prev => !prev);
    }
  };
  
  const handleScheduleBooking = async (scheduleData) => {
    try {
      setIsProcessingSchedule(true);
      const schedulesRef = dbRef(db, 'schedules');
      const newScheduleRef = push(schedulesRef);
      await set(newScheduleRef, scheduleData);
      showToast('Schedule request submitted successfully!', 'success');
      setShowScheduleModal(false);
    } catch (err) {
      console.error('Error submitting schedule:', err);
      showToast('Error submitting schedule request', 'error');
    } finally {
      setIsProcessingSchedule(false);
    }
  };

  // Artist registration button click
  const handleArtistRegisterClick = () => {
    if (!currentUser) {
      showToast('Please sign in to register as an artist', 'info');
      setShowAdminLogin(true);
      return;
    }
    setShowArtistSubscription(true);
  };

  // Select subscription tier and initiate Paddle checkout
  const handleSelectArtistSubscription = async (tier, artistInfo) => {
    if (!currentUser) {
      showToast('Please sign in first', 'error');
      return;
    }

    try {
      const result = await createSubscription(
        currentUser.uid,
        artistInfo.name,
        currentUser.email,
        tier
      );

      if (result.checkoutUrl) {
        showToast('Redirecting to Paddle checkout...', 'success');
        window.location.href = result.checkoutUrl;
      } else {
        showToast('Error initiating subscription. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      showToast(`Error: ${error.message}`, 'error');
    }
  };

  // Toast helper
  const showToast = (message, type = 'success', duration = 3000) => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: '' }), duration);
  };

  // Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthReady(true);
      setCurrentUser(user);
      if (user) {
        const isAuthAdmin = AUTHORIZED_ADMIN_EMAILS.includes(user.email);
        setIsAdmin(isAuthAdmin);

        // Persist account details to the users table
        syncUserProfile(user);

        const artistRef = dbRef(db, `artists/${user.uid}`);
        const artistUnsubscribe = onValue(artistRef, (snapshot) => {
          const data = snapshot.val();
          setArtistData(data);
        });

        return () => {
          artistUnsubscribe();
        };
      } else {
        setIsAdmin(false);
        setArtistData(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch all content from Firebase
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);

        const heroRef = dbRef(db, 'content/heroImage');
        onValue(heroRef, (snap) => {
          setHeroImageUrl(snap.val() || '');
        });

        const videosRef = dbRef(db, 'content/videos');
        onValue(videosRef, (snap) => {
          const data = snap.val();
          const formattedVideos = data ? Object.entries(data).map(([key, value]) => ({...value, key})) : [];
          setVideos(formattedVideos);
        });

        const musicRef = dbRef(db, 'content/music');
        onValue(musicRef, (snap) => {
          const data = snap.val();
          const formattedMusic = data ? Object.entries(data).map(([key, value]) => ({...value, key})) : [];
          
          const artistMusicRef = dbRef(db, 'artistContent/music');
          onValue(artistMusicRef, (artistSnap) => {
            const artistData = artistSnap.val();
            const formattedArtistMusic = artistData 
              ? Object.entries(artistData)
                  .map(([key, value]) => ({...value, key}))
                  .filter(item => item.status === 'approved')
              : [];
            
            setMusic([...formattedMusic, ...formattedArtistMusic]);
          });
        });

        const membersRef = dbRef(db, 'content/members');
        onValue(membersRef, (snap) => {
          const data = snap.val();
          const formattedMembers = data ? Object.entries(data).map(([key, value]) => ({...value, key})) : [];
          setMembers(formattedMembers);
        });

        const schedulesRef = dbRef(db, 'schedules');
        onValue(schedulesRef, (snap) => {
          const data = snap.val();
          const formattedSchedules = data ? Object.entries(data).map(([key, value]) => ({...value, key})) : [];
          setSchedules(formattedSchedules);
        });

        const galleryRef = dbRef(db, 'content/gallery');
        onValue(galleryRef, (snap) => {
          const data = snap.val();
          const formattedGallery = data ? Object.entries(data).map(([key, value]) => ({...value, key})) : [];
          setGallery(formattedGallery);
        });

        setLoading(false);
      } catch (err) {
        console.error('Error fetching content:', err);
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Google Admin login
  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Persist account details to the users table on register/sign-in
      syncUserProfile(user, result.additionalUserInfo?.isNewUser);

      if (AUTHORIZED_ADMIN_EMAILS.includes(user.email)) {
        showToast(`Welcome Admin, ${user.displayName}!`, 'success');
        setShowAdminLogin(false);
        setIsAdmin(true);
        navigate('/admin?tab=dashboard');
      } else {
        showToast(`Welcome, ${user.displayName}!`, 'info');
        setShowAdminLogin(false);
        navigate('/');
      }
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') {
        showToast('Sign-in canceled.', 'info', 1500);
      } else {
        console.error('Google Sign-in Error:', err);
        showToast('Authentication failed.', 'error');
      }
    }
  };

  // Admin logout
  const handleAdminLogout = async () => {
    try {
      await firebaseSignOut(auth);
      setIsAdmin(false);
      setCurrentUser(null);
      setArtistData(null);
      showToast('Logged out successfully!', 'info');
      navigate('/');
    } catch (err) {
      showToast('Logout failed', 'error');
    }
  };

  const handleBookScheduleClick = () => {
    if (!currentUser) {
      showToast('Please sign in to book a schedule', 'info');
      setShowAdminLogin(true);
      return;
    }
    setShowScheduleModal(true);
  };

  const handleDonateClick = () => {
    setShowDonateModal(true);
  };

  return (
    <AudioPlayerProvider currentUser={currentUser}>
      <div className="flex flex-col min-h-screen bg-[#08080a] text-white font-sans antialiased selection:bg-[#bd9f67] selection:text-black">
        {isAdminRoute ? (
          <AdminNavigation
            currentUser={currentUser}
            onLogout={handleAdminLogout}
          />
        ) : (
          <Navigation
            isAdmin={isAdmin}
            onAdminClick={handleNavigationAdminClick}
            onLogout={handleAdminLogout}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            userDisplayName={currentUser?.displayName}
            userPhotoURL={currentUser?.photoURL}
            currentUser={currentUser}
            onArtistRegisterClick={handleArtistRegisterClick}
            artistData={artistData}
            onBookScheduleClick={handleBookScheduleClick}
            onDonateClick={handleDonateClick}
          />
        )}

        {/* Luxury Admin/User Login Modal */}
        <AnimatePresence>
          {showAdminLogin && !isAdmin && (
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAdminLogin(false)}
            >
              <motion.div
                className="bg-[#121318]/90 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl p-6 md:p-8 max-w-sm w-full relative overflow-hidden"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={e => e.stopPropagation()}
              >
                {/* Gold Glow Decor */}
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#bd9f67]/20 rounded-full blur-2xl pointer-events-none" />

                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-2 text-[#bd9f67]">
                    <span className="text-xs font-semibold tracking-wider uppercase">Account Sign In</span>
                  </div>
                  <button
                    onClick={() => setShowAdminLogin(false)}
                    className="p-1 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition"
                  >
                    <X size={18} />
                  </button>
                </div>

                <h2 className="text-xl font-bold tracking-tight text-white mb-2 text-center">
                  Welcome Back
                </h2>
                <p className="text-xs text-white/60 text-center mb-6 leading-relaxed">
                  Sign in with Google to manage your account, store purchases, and access features.
                </p>

                <button
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#bd9f67] hover:bg-[#d0b07a] text-gray-950 rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-[#bd9f67]/20 text-sm"
                >
                  <img 
                    src="https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp=s96-fcrop64=1,00000000ffffffff-rw" 
                    alt="Google" 
                    className="w-4 h-4" 
                  />
                  <span>Sign in with Google</span>
                </button>

                <button
                  onClick={() => setShowAdminLogin(false)}
                  className="w-full mt-3 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/70 rounded-xl font-medium transition text-xs border border-white/10"
                >
                  Cancel
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modals */}
        <ArtistSubscriptionModal
          isOpen={showArtistSubscription}
          onClose={() => setShowArtistSubscription(false)}
          onSelectSubscription={handleSelectArtistSubscription}
          isProcessing={isProcessingArtist}
          currentUser={currentUser}
        />

        <ScheduleBookingModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onSubmit={handleScheduleBooking}
          isProcessing={isProcessingSchedule}
          currentUser={currentUser}
        />

        <DonationModal
          isOpen={showDonateModal}
          onClose={() => setShowDonateModal(false)}
        />

        {/* Artist Dashboard Modal */}
        <AnimatePresence>
          {showArtistDashboard && currentUser && artistData && (
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowArtistDashboard(false)}
            >
              <motion.div
                className="bg-[#121318] border border-white/10 text-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                onClick={e => e.stopPropagation()}
              >
                <ArtistDashboard
                  db={db}
                  currentUser={currentUser}
                  showToast={showToast}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {toast.message && (
          <Toast message={toast.message} type={toast.type} />
        )}

        {/* Routes Container */}
        <div className="flex-1">
          <Routes>
            <Route 
              path="/" 
              element={
                <PublicView
                  heroImageUrl={heroImageUrl}
                  gallery={gallery}
                  music={music}
                  videos={videos}
                  members={members}
                  schedules={schedules}
                  loading={loading}
                  currentUser={currentUser}
                  onArtistRegisterClick={handleArtistRegisterClick}
                  onBookScheduleClick={handleBookScheduleClick}
                  onDonateClick={handleDonateClick}
                />
              } 
            />

            <Route 
              path="/admin" 
              element={
                isAdmin ? (
                  <AdminView
                    db={db}
                    showToast={showToast}
                    videos={videos}
                    music={music}
                    members={members}
                    gallery={gallery}
                    heroImageUrl={heroImageUrl}
                    currentUser={currentUser}
                  />
                ) : authReady ? (
                  <Navigate to="/" replace />
                ) : (
                  <div className="min-h-screen flex items-center justify-center text-white/50 text-sm">
                    Checking admin access...
                  </div>
                )
              } 
            />

            <Route 
              path="/music" 
              element={
                <MusicPage 
                  music={music}
                  loading={loading}
                  currentUser={currentUser}
                  showToast={showToast}
                />
              } 
            />
            <Route 
              path="/videos" 
              element={
                <VideosPage 
                  videos={videos}
                  loading={loading}
                />
              } 
            />
            <Route 
              path="/gallery" 
              element={
                <GalleryPage 
                  gallery={gallery}
                  loading={loading}
                />
              } 
            />
            <Route 
              path="/members" 
              element={
                <MembersPage 
                  members={members}
                  loading={loading}
                />
              } 
            />
            <Route 
              path="/purchases" 
              element={
                <PurchasesPage 
                  currentUser={currentUser}
                  purchases={purchases}
                  showToast={showToast}
                />
              } 
            />
            <Route 
              path="/my-library" 
              element={
                <AllPurchasesPage 
                  music={music}
                  currentUser={currentUser}
                  purchases={purchases}
                  showToast={showToast}
                />
              } 
            />

            <Route 
              path="/schedules" 
              element={
                <SchedulesListPage 
                  db={db}
                  currentUser={currentUser}
                  showToast={showToast}
                  isAdmin={isAdmin}
                />
              } 
            />

            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancel" element={<PaymentCancel />} />
            <Route path="/subscription-success" element={<PaymentSuccessArtist showToast={showToast} />} />
            <Route path="/subscription-cancel" element={<PaymentCancelArtist />} />
            <Route path="/artist-dashboard" element={<ArtistDashboardPage showToast={showToast} />} />
          </Routes>
        </div>

        {/* Footer */}
        {!isAdminRoute && <Footer />}
      </div>
    </AudioPlayerProvider>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}